import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, of, switchMap } from 'rxjs';
import { BackendApiService } from '../../../../shared/services/backend-api.service';
import { ServiceResponse } from '../../../../shared/models/serviceResponse.model';
import { AdminStateService, Client } from '../../../../shared/services/admin-state.service';
import { AuthService } from '../../../../shared/services/auth.service';
import { showNotification } from '../../../../shared/components/app-notifications/app-notifications.component';
import { RenameClientDialogComponent } from './rename-client-dialog/rename-client-dialog.component';
import { DeleteClientDialogComponent } from './delete-client-dialog/delete-client-dialog.component';

interface ClientConfiguration {
  customLogo: string;
  customWebSite: string;
  customEmail: string;
  customLastNodeName: string;
  excelSingleSpreadsheet: boolean;
  bomConfigId: string;
}

interface ProjectStructure {
  name: string;
  description: string;
  img: string;
  subOptions: any;
  status: string;
  nodes: any[];
  models: any[] | null;
}

@Component({
  selector: 'app-admin-dashboard',
  templateUrl: './admin-dashboard.component.html',
  styleUrls: ['./admin-dashboard.component.scss'],
})
export class AdminDashboardComponent implements OnInit {
  constructor(
    private router: Router,
    private backendApiService: BackendApiService,
    private adminStateService: AdminStateService,
    private authService: AuthService,
    private modalService: NgbModal
  ) {}

  clients: Client[] = [];
  selectedClient: Client | null = null;
  isLoadingClients = false;
  isLoadingClientDetails = false;
  // Clients with a rename/delete in flight; each operation removes only its own id
  busyClientIds = new Set<string>();

  ngOnInit(): void {
    this.loadClients();
  }

  loadClients(): void {
    this.isLoadingClients = true;
    this.backendApiService.getAllClients().subscribe({
      next: (response: ServiceResponse<Client[]>) => {
        if (response.isSuccess && response.result) {
          this.clients = response.result;
        }
        this.isLoadingClients = false;
      },
      error: (error: any) => {
        console.error('Error loading clients', error);
        this.isLoadingClients = false;
      }
    });
  }

  selectClient(clientId: string): void {
    this.isLoadingClientDetails = true;
    this.backendApiService.getClientById(clientId).subscribe({
      next: (response: ServiceResponse<Client>) => {
        if (response.isSuccess && response.result) {
          // Store the complete client information in both services
          this.adminStateService.setSelectedClient(response.result);
          this.authService.updateClientInfo(response.result);
          this.selectedClient = response.result;

          // Navigate to user page
          this.router.navigate(['/user']);
        }
        this.isLoadingClientDetails = false;
      },
      error: (error: any) => {
        console.error('Error loading specific client', error);
        this.isLoadingClientDetails = false;
      }
    });
  }

  onRowClick(client: Client): void {
    if (this.isLoadingClientDetails || this.isBusy(client.id)) {
      return;
    }
    this.selectClient(client.id);
  }

  // Enter on a button inside the row bubbles up here; only react when the row itself is focused
  onRowKeydown(event: Event, client: Client): void {
    if (event.target === event.currentTarget) {
      this.onRowClick(client);
    }
  }

  openRenameDialog(client: Client): void {
    const modalRef = this.modalService.open(RenameClientDialogComponent, {
      backdrop: 'static',
      size: 'md'
    });
    modalRef.componentInstance.currentName = client.name;
    modalRef.result.then(
      (newName: string) => this.renameClient(client, newName),
      () => {}
    );
  }

  // UpdateClient replaces the whole document, so load the full client first and change only the name
  renameClient(client: Client, newName: string): void {
    this.busyClientIds.add(client.id);
    this.backendApiService.getClientById(client.id).pipe(
      switchMap((response: ServiceResponse<Client>) =>
        response.isSuccess && response.result
          ? this.backendApiService.updateClient({ ...response.result, name: newName })
          : of(response)
      ),
      finalize(() => this.busyClientIds.delete(client.id))
    ).subscribe({
      next: (response: ServiceResponse<Client>) => {
        if (response.isSuccess && response.result) {
          const updated = response.result;
          this.clients = this.clients.map(c =>
            c.id === client.id ? { ...c, name: updated.name, version: updated.version } : c);
          if (this.adminStateService.getSelectedClient()?.id === client.id) {
            this.adminStateService.setSelectedClient(updated);
            this.authService.updateClientInfo(updated);
          }
          showNotification('success', 'Success', `Client renamed to "${updated.name}"`, 5);
        } else {
          showNotification('error', 'Error', this.firstMessage(response, 'Failed to rename client'), 5);
        }
      },
      error: (error: any) => {
        console.error('Error renaming client', error);
        showNotification('error', 'Error', 'Failed to rename client. Please try again.', 5);
      }
    });
  }

  openDeleteDialog(client: Client): void {
    const modalRef = this.modalService.open(DeleteClientDialogComponent, {
      backdrop: 'static',
      size: 'md'
    });
    modalRef.componentInstance.clientName = client.name;
    modalRef.result.then(
      (confirmed: boolean) => {
        if (confirmed) {
          this.deleteClient(client);
        }
      },
      () => {}
    );
  }

  deleteClient(client: Client): void {
    this.busyClientIds.add(client.id);
    this.backendApiService.deleteClient(client.id).pipe(
      finalize(() => this.busyClientIds.delete(client.id))
    ).subscribe({
      next: (response: ServiceResponse<boolean>) => {
        if (response.isSuccess) {
          this.clients = this.clients.filter(c => c.id !== client.id);
          if (this.adminStateService.getSelectedClient()?.id === client.id) {
            this.adminStateService.clearSelectedClient();
            this.selectedClient = null;
          }
          showNotification('success', 'Success', `Client "${client.name}" deleted`, 5);
        } else {
          showNotification('error', 'Error', this.firstMessage(response, 'Failed to delete client'), 5);
        }
      },
      error: (error: any) => {
        console.error('Error deleting client', error);
        showNotification('error', 'Error', 'Failed to delete client. Please try again.', 5);
      }
    });
  }

  isBusy(clientId: string): boolean {
    return this.busyClientIds.has(clientId);
  }

  private firstMessage(response: ServiceResponse<any>, fallback: string): string {
    return response.messages?.[0]?.content || fallback;
  }

  goToQuestionsPage(): void {
    this.router.navigate(['/admin/communications']);
  }

  isBaseAdminRoute(): boolean {
    return this.router.url === '/admin';
  }
}
